// De schil-bouwstenen: Zoekveld (patroon 9), Zijbalk + NavKnop (patroon 5),
// Rij en TabelKop (patroon 10).

import type { LucideIcon } from "lucide-react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { forwardRef, type KeyboardEvent, type ReactNode } from "react";

import { Icoon } from "./basis";
import { Sleepgreep, vars } from "./lagen";

// ── Zoekveld ───────────────────────────────────────────────────────────────

export const Zoekveld = forwardRef<
  HTMLInputElement,
  { waarde: string; onWaarde: (v: string) => void; plaatshouder: string; sneltoets?: boolean }
>(function Zoekveld({ waarde, onWaarde, plaatshouder, sneltoets = true }, ref) {
  return (
    <label className="s-zoekveld">
      <Icoon als={Search} />
      <span className="s-verborgen">Zoeken</span>
      <input
        ref={ref}
        type="search"
        data-lat="zoekveld"
        placeholder={plaatshouder}
        value={waarde}
        onChange={(e) => onWaarde(e.target.value)}
      />
      {sneltoets && <kbd className="s-toets s-alleen-breed">Ctrl K</kbd>}
    </label>
  );
});

// ── Zijbalk ────────────────────────────────────────────────────────────────

export function Zijbalk({
  kop,
  voet,
  children,
  label = "Hoofdmenu",
  rail = false,
  breedte,
  greep,
}: {
  kop: ReactNode;
  voet: ReactNode;
  children: ReactNode;
  label?: string;
  /** Ingeklapt: alleen de iconen blijven staan (zoals Codex). */
  rail?: boolean;
  /** Breedte in px tijdens en na het slepen. */
  breedte?: number;
  /** Greep op de rechterrand: slepen, loslaten (vastklikken of inklappen), reset. */
  greep?: { onSleep: (x: number) => void; onLos: (x: number) => void; onReset: () => void };
}) {
  return (
    <nav
      className={`s-zijbalk${rail ? " s-zijbalk--rail" : ""}`}
      data-lat="zijbalk"
      aria-label={label}
      style={breedte && !rail ? vars({ "--zijbalk-b": `${breedte}px` }) : undefined}
    >
      {greep && <Sleepgreep kant="rechts" label="Breedte van de zijbalk" onSleep={greep.onSleep} onLos={greep.onLos} onReset={greep.onReset} />}
      <div className="s-zijbalk__kop">{kop}</div>
      <div className="s-zijbalk__nav">{children}</div>
      <div className="s-zijbalk__voet">{voet}</div>
    </nav>
  );
}

export function NavKnop({
  icoon,
  children,
  actief = false,
  sub = false,
  open,
  onClick,
}: {
  icoon?: LucideIcon;
  children: ReactNode;
  actief?: boolean;
  sub?: boolean;
  /** Heeft sub-items: dan een pijltje dat open/dicht toont. */
  open?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`s-nav${sub ? " s-nav--sub" : ""}`}
      aria-current={actief ? "page" : undefined}
      aria-expanded={open}
      title={typeof children === "string" ? children : undefined}
      onClick={onClick}
    >
      {icoon && <Icoon als={icoon} />}
      <span className="s-afkap">{children}</span>
      {open !== undefined && (
        <span className="s-nav__pijl">
          <Icoon als={open ? ChevronDown : ChevronRight} klein />
        </span>
      )}
    </button>
  );
}

// ── Rij en tabel ───────────────────────────────────────────────────────────

/**
 * Eén klikbare rij. `kolommen` is een grid-template (rekenwaarde, gaat als
 * CSS-variabele mee); zonder kolommen is de rij één flexregel.
 */
export function Rij({
  onKies,
  gekozen = false,
  dicht = false,
  kolommen,
  children,
  label,
  className,
}: {
  onKies: () => void;
  gekozen?: boolean;
  dicht?: boolean;
  kolommen?: string;
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  const toets = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onKies();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-current={gekozen ? "true" : undefined}
      data-lat="rij"
      className={`s-rij${dicht ? " s-rij--dicht" : ""}${kolommen ? " s-rij--raster" : ""}${className ? ` ${className}` : ""}`}
      style={kolommen ? vars({ "--kolommen": kolommen }) : undefined}
      onClick={onKies}
      onKeyDown={toets}
    >
      {children}
    </div>
  );
}

export function TabelKop({ kolommen, children }: { kolommen: string; children: ReactNode }) {
  return (
    <div className="s-tabel__kop" style={vars({ "--kolommen": kolommen })}>
      {children}
    </div>
  );
}
